import { LoadingIndicator } from "@/components/LoadingIndicator";
import {
  Button,
  ChevronLeftIcon as ChevronLeft,
  ChevronRightIcon as ChevronRight,
  GradeIcon as Grade,
  IconButton,
  RemoveIcon as Remove,
  TuneIcon as Tune,
  Typography,
} from "@iluvatar/ui-kit";

import { SwipeableDrawer } from "@iluvatar/ui-kit";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { useSwipeable } from "react-swipeable";

import { ErrorBoBoMessage } from "@/components/ErrorBoBoMessage";
import { FlexSpacer } from "@/components/FlexSpacer";
import { MessageContainer } from "@/components/MessageContainer";
import {
  defaultSearchSettings,
  type SearchSettings,
} from "@/components/search-settings";
import { ViewProfile } from "@/components/ViewProfile";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { useSearchQuery } from "@/hooks/useSearchQuery";
import { useToggle } from "@/hooks/useToggle";
import DefaultLayout from "@/layout/default-page";
import { randomizeGeolocation } from "@/utilities/geolocation";

const SearchFiltersForm = lazy(() =>
  import("@/components/SearchFiltersForm").then((module) => ({
    default: module.SearchFiltersForm,
  })),
);

const PREFETCH_REMAINING_PROFILES = 2;

function HomePage() {
  const { user, api } = useCurrentUser();
  const [actionError, setActionError] = useState<unknown>();
  const [loaders, setLoaders] = useState({ like: false });
  const [requestedIndex, setViewIdx] = useState(0);
  const [searchDrawerOpen, toggleSearchDrawer] = useToggle(false);
  const [searchSettings, setSearchSettings] = useLocalStorage<SearchSettings>(
    `search-storage-${user?.uid || "pending"}`,
    defaultSearchSettings,
  );
  const {
    data: values,
    error,
    loadMore,
    hideUser: removeUser,
    ...query
  } = useSearchQuery({
    communityId: "default",
    age: searchSettings.age,
    gender: searchSettings.gender,
    sexuality: searchSettings.sexuality,
    locationId: searchSettings.locationId,
    geo: searchSettings.geolocation
      ? {
          dist: searchSettings.geoDist,
          units: searchSettings.geoUnits,
        }
      : undefined,
  });
  const viewIdx = Math.min(requestedIndex, Math.max(values.length - 1, 0));

  if (requestedIndex !== viewIdx) {
    setViewIdx(viewIdx);
  }

  useEffect(() => {
    if (
      values.length - viewIdx <= PREFETCH_REMAINING_PROFILES &&
      query.hasNextPage &&
      !query.isFetching &&
      !query.isError
    ) {
      loadMore();
    }
  }, [
    values.length,
    viewIdx,
    query.hasNextPage,
    query.isFetching,
    query.isError,
    loadMore,
  ]);

  useQuery({
    queryKey: ["geolocation", user?.uid],
    enabled: Boolean(user?.uid && "geolocation" in navigator),
    queryFn: async () => {
      const position = await new Promise<GeolocationPosition>(
        (resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: false,
            timeout: 30000,
            maximumAge: 1000,
          });
        },
      );
      const geolocation = randomizeGeolocation(position.coords);
      if (user) {
        await api.updateUser(user.uid, { geolocation });
      }
      return geolocation;
    },
    retry: false,
    refetchOnWindowFocus: false,
  });

  const prevClick = useCallback(() => {
    setViewIdx((_idx) => (_idx > 0 ? _idx - 1 : _idx));
  }, []);

  const nextClick = useCallback(() => {
    setViewIdx((_idx) => Math.min(_idx + 1, Math.max(values.length - 1, 0)));
  }, [values.length]);

  const swipeProps = useSwipeable({
    onSwipedRight: prevClick,
    onSwipedLeft: nextClick,
  });

  const likeUser = useCallback(
    function likeUserHandler(userId: string) {
      return async function innerClickHandler() {
        setActionError(undefined);
        setLoaders((s) => ({ ...s, like: true }));
        try {
          await api.likeUser(userId);
          removeUser(userId);
        } catch (actionFailure) {
          setActionError(actionFailure);
        } finally {
          setLoaders((s) => ({ ...s, like: false }));
        }
      };
    },
    [api, removeUser],
  );

  const hideUser = useCallback(
    function ignoreUserHandler(userId: string) {
      return async function innerClickHandler() {
        setActionError(undefined);
        setLoaders((s) => ({ ...s, like: true }));
        try {
          await api.ignoreUser(userId);
          removeUser(userId);
        } catch (actionFailure) {
          setActionError(actionFailure);
        } finally {
          setLoaders((s) => ({ ...s, like: false }));
        }
      };
    },
    [api, removeUser],
  );

  return (
    <DefaultLayout
      {...swipeProps}
      className={
        "profile-container ml-auto mr-auto [max-width:900px] flex flex-col [background:linear-gradient(180deg,_transparent_0%,_transparent_50%,_var(--color-background-default)_50%,_var(--color-background-default))] [@media(min-width:_900px)]:[--page-content-padding:42px] [@media(min-width:_900px)]:[padding-left:42px] [@media(min-width:_900px)]:[padding-right:42px] [&.profile-container--input]:[max-width:900px] [&.profile-container--input]:w-full [@media(max-width:_400px)]:[&.main-container]:[--page-content-padding:8px] [@media(max-width:_400px)]:[&.main-container]:[padding-left:8px] [@media(max-width:_400px)]:[&.main-container]:[padding-right:8px] mb-[76px] pb-[24px]"
      }
      style={{
        height:
          values?.length == 0
            ? "calc(100vh - var(--nav-height) - 76px - 1px)"
            : "",
      }}
    >
      <ErrorBoBoMessage error={error || actionError} />
      {query.isFetchNextPageError && (
        <Button
          variant="outline"
          onClick={loadMore}
          disabled={query.isFetching}
        >
          Retry loading profiles
        </Button>
      )}

      {!query.isFetching &&
        !query.hasNextPage &&
        !error &&
        values.length === 0 && (
          <MessageContainer>
            <Typography variant="h2">Oh, no people found.</Typography>
            <Typography className="[margin-top:8px]">
              This must be one of those awkward silences humans have,
              <br /> but for computers.
            </Typography>
            <Typography variant="caption" className="[margin-top:24px]">
              You may see more people by changing your
              <a onClick={toggleSearchDrawer(true)} className="dumb-button">
                {` filters `}
              </a>
              below.
            </Typography>
          </MessageContainer>
        )}

      {(query.isFetching || (query.hasNextPage && !error)) &&
        values.length === 0 && (
          <MessageContainer>
            <LoadingIndicator size={64} aria-label="Loading people" />
          </MessageContainer>
        )}

      {Boolean(values.length && values[viewIdx]) && (
        <ViewProfile
          communityId="default"
          distance={
            values[viewIdx]?.distance
              ? `${values[viewIdx]?.distance}${values[viewIdx]?.distanceUnits}`
              : undefined
          }
          userId={values[viewIdx]?.uid}
          profile={values[viewIdx]?.profile}
          about={values[viewIdx]}
        />
      )}

      <div className="page-frame-actions discovery-actions w-[100%] min-h-[64px] fixed bottom-0 left-0 bg-[var(--color-primary--active)] px-4 z-100 backdrop-blur">
        <div className="flex mx-auto w-full max-w-screen-md my-4 gap-4 z-100 items-center justify-center">
          <IconButton
            variant="ghost"
            label="Filter profiles"
            onClick={toggleSearchDrawer(true)}
            className="text-[var(--color-text)]"
          >
            <Tune />
          </IconButton>
          <IconButton
            variant="ghost"
            label="Previous profile"
            disabled={!(viewIdx > 0)}
            onClick={prevClick}
            className="text-[var(--color-text)]"
          >
            <ChevronLeft />
          </IconButton>
          <IconButton
            variant="ghost"
            label="Next profile"
            disabled={!(values.length - 1 - viewIdx > 0)}
            onClick={nextClick}
            className="text-[var(--color-text)]"
          >
            <ChevronRight />
          </IconButton>

          <FlexSpacer />
          {Boolean(values && values[viewIdx]) && (
            <Button
              variant="outline"
              aria-label="Ignore profile"
              disabled={loaders.like}
              onClick={hideUser(values[viewIdx]?.uid)}
              className={[
                "bg-[var(--color-primary--hover)] p-[9px] min-w-0",
                "[border-radius:12px] [color:rgb(var(--uik-info))]",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <Remove />
            </Button>
          )}
          {Boolean(values && values[viewIdx]) && (
            <div>
              <Button
                variant="outline"
                aria-label="Like profile"
                onClick={likeUser(values[viewIdx].uid)}
                disabled={loaders.like}
                className={[
                  "border-[var(--color-chart--warning)] p-[9px] min-w-0 hover:bg-[var(--color-chart--warning)] hover:border-[var(--color-border)]",
                  "[border-radius:12px]",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <Grade className="[color:rgb(var(--uik-info))]" />
              </Button>
            </div>
          )}
        </div>
      </div>
      <SwipeableDrawer
        open={searchDrawerOpen}
        onOpenChange={(next) => toggleSearchDrawer(next)()}
        direction="bottom"
        title="Filters & Preferences"
      >
        <div className="mx-auto w-full max-w-screen-md">
          {searchDrawerOpen && (
            <Suspense fallback={<div role="status">Loading filters…</div>}>
              <SearchFiltersForm
                id="search-settings"
                initialValues={searchSettings}
                onCancel={toggleSearchDrawer(false)}
                onSubmit={async (ev) => {
                  if (ev.geolocation && user) {
                    await api.updateUser(user?.uid, {
                      geolocation: {
                        latitude: ev.geolocation.lat,
                        longitude: ev.geolocation.long,
                      },
                    });
                  }
                  setViewIdx(0);
                  setSearchSettings(ev);
                  toggleSearchDrawer(false)();
                }}
              />
            </Suspense>
          )}
        </div>
      </SwipeableDrawer>
    </DefaultLayout>
  );
}

export const Route = createFileRoute("/")({
  component: HomePage,
});
